import React, { useCallback, useMemo } from 'react';

import type {
    GridApi,
    RowClassParams,
    RowClickedEvent,
} from 'ag-grid-community';
import _ from 'lodash';

import AGTable from '../../../../../../components/ag-table/AGTable';
import Spinner from '../../../../../../components/loading/nestedSpinner';
import styled from 'styled-components';

import aampeTheme from '../../../../../../constants/aampeTheme';

import useGetDatasetGridInfo from '../../../../../../hooks/datasets/useGetDatasetGridInfo';

import {
    DatasetFiltersModel,
    DatasetFiltersType,
} from '../../../../../../models/dataset-filters.model';
import { DatasetModel } from '../../../../../../models/dataset.model';

interface DatasetContentTableProps {
    isEditable: boolean;
    datasetId: number;
    setRowCount: (count: number) => void;
    datasetFilters?: DatasetFiltersType;
    setDatasetFilters: (count: DatasetFiltersType) => void;
    filteredDataset?: DatasetModel;
    datasetLoading: boolean;
    onRowClicked?: (event: RowClickedEvent) => void;
    selectedDataset?: DatasetFiltersType;
}

const DatasetContentTable: React.FC<
    React.PropsWithChildren<DatasetContentTableProps>
> = ({
    isEditable,
    datasetId,
    setRowCount,
    datasetFilters,
    setDatasetFilters,
    onRowClicked,
    selectedDataset,
}) => {
    const gridData = useGetDatasetGridInfo({
        datasetId,
        editable: false,
        menuTabs: isEditable ? ['filterMenuTab'] : [],
    });

    const handleFilterChanged = (
        api: GridApi,
        setRowCount: (count: number) => void,
        setDatasetFilters?: (filters: Record<string, string[]>) => void,
    ) => {
        setRowCount(api.getDisplayedRowCount());

        const model = api.getFilterModel();
        const filters: DatasetFiltersType = {};

        Object.entries(model).forEach(([colKey, filter]: [string, any]) => {
            let values: string[] = [];

            if (filter?.filterType === 'set') {
                values = filter.values || [];
            } else if (filter?.filterType === 'text') {
                if (filter.filter) {
                    values = [filter.filter];
                }
            }

            if (values.length > 0) {
                const column = api.getColumn(colKey);

                const currentColField = column?.getColDef()?.field;

                if (currentColField) {
                    filters[currentColField] = values;
                }
            }
        });

        setDatasetFilters?.(filters);
    };

    const createInitialFilterModel = (
        api: GridApi,
        datasetFilters: DatasetFiltersType,
    ) => {
        const model: Record<string, any> = {};

        Object.entries(datasetFilters).forEach(([colIndex, values]) => {
            const column = api
                .getColumns()
                ?.find((col) => col.getColDef().field === colIndex);

            if (column) {
                model[column.getColId()] = {
                    filterType: 'set',
                    values,
                };
            }
        });

        return model;
    };
    const getRowStyle = useCallback(
        (params: RowClassParams<DatasetFiltersModel>) => {
            const isSelected = _.isEqual(
                _.omit(params.data, ['id']),
                _.omit(selectedDataset, ['id']),
            );
            return {
                backgroundColor: isSelected
                    ? aampeTheme.colors.interactiveWG01
                    : aampeTheme.colors.white,
            };
        },
        [selectedDataset],
    );

    const isDataLoading = useMemo<boolean>(
        () =>
            gridData.isDatasetLoading ||
            gridData.isDatasetContentLoading ||
            gridData.isLabelsByComponentTypeLoading ||
            gridData.isColumnComponentTypesLoading,
        [gridData],
    );

    if (isDataLoading) return <StyledSpinner />;

    return (
        <Container>
            <StyledAGTable
                rowData={gridData.rowsData}
                columns={gridData.columnsConfig}
                isDataLoading={isDataLoading}
                customOnModelUpdated={(event) => {
                    setRowCount(event.api.getDisplayedRowCount());
                }}
                onFilterChanged={(api) => {
                    handleFilterChanged(api, setRowCount, setDatasetFilters);
                }}
                customOnFirstData={(event) => {
                    if (datasetFilters) {
                        event.api.setFilterModel(
                            createInitialFilterModel(event.api, datasetFilters),
                        );
                    }
                }}
                onRowClicked={onRowClicked}
                getRowStyle={getRowStyle}
            />
        </Container>
    );
};
export default DatasetContentTable;

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    gap: aampeTheme.spacing4(6),
    width: '100%',
    overflow: 'hidden',
});

const StyledAGTable = styled(AGTable)({
    margin: 0,
    padding: 0,
    width: '100%',
});

const StyledSpinner = styled(Spinner)({ minHeight: 250 });
